/**
 * useActingAccountId — the organization this tab is actually looking at, live.
 *
 * Thin wiring over the existing onAccountMembershipAccountIdSnapshot
 * (repositories/walletRepository.ts), which already resolves the back-pointer
 * chain through pickBackPointerAccountId and then through resolveTabWorkspace,
 * so a tab pinned to one organization is not dragged to another by a switch on
 * a different device. Same idiom as WalletExpensesContext.
 *
 * Used by the Team & Roles surfaces as the FALLBACK account id: the callables
 * that return `resolvedAccountId` (listMembers, listInvitations,
 * getUsageLimits) are the authority, because that is literally the account the
 * rendered data came from. rbacListRoles is the one sibling that returns no
 * such field, so the roles surface resolves it here instead.
 */
import { useEffect, useState } from 'react';
import { useMaybeAuth } from '../context/AuthContext';
import { onAccountMembershipAccountIdSnapshot } from '../repositories/walletRepository';

export function useActingAccountId(): string | null {
  const auth = useMaybeAuth();
  const uid = auth?.user?.id ?? null;
  const [accountId, setAccountId] = useState<string | null>(null);

  useEffect(() => {
    // Reset BEFORE the guard so a previous identity's organization never
    // lingers for a frame (the VmLifecycleBanner lesson).
    setAccountId(null);
    if (!uid) return;
    let unsub: (() => void) | null = null;
    try {
      unsub = onAccountMembershipAccountIdSnapshot(
        uid,
        (next) => setAccountId(next),
        () => setAccountId(null),
      );
    } catch {
      unsub = null;
    }
    return () => { if (unsub) unsub(); };
  }, [uid]);

  return accountId;
}

export default useActingAccountId;
